/**
 * Client-side asset access over the player's mounted archives, with decode caches.
 * All content originates from the player's own discs.
 */
import {
  type ArchiveSet,
  decodeAnimData,
  decodeDc6,
  decodeFontTable,
  decodePaletteDat,
  decodePl2,
  detectLanguage,
  ExcelTable,
  type AnimRecord,
  type Pl2,
  stitchDc6,
  loadStrings,
  type StringTable,
} from '@d2/formats';
import { Sprite } from '../gfx/atlas';
import type { Renderer } from '../gfx/renderer';
import { Font } from './font';

export const PALETTES = ['Units', 'ACT1', 'ACT2', 'ACT3', 'ACT4', 'ACT5', 'Sky', 'Loading', 'Static', 'Trademark', 'fechar', 'Menu0', 'EndGame'] as const;
export type PaletteName = (typeof PALETTES)[number];

/** D2 text colors in the order of the `ÿc` codes (0–9, ':', ';', '<'). */
export const enum TextColor {
  White = 0,
  Red = 1,
  Green = 2,
  Blue = 3,
  Gold = 4,
  Grey = 5,
  Black = 6,
  Tan = 7,
  Orange = 8,
  Yellow = 9,
  DarkGreen = 10,
  Purple = 11,
  BrightGreen = 12,
}

export class Assets {
  readonly palette = {} as Record<PaletteName, number>;
  pl2!: Pl2;
  strings!: StringTable;
  /** Language folder of the player's copy ("eng", "deu"…): strings and localized UI pictures. */
  language = 'eng';
  animData!: Map<string, AnimRecord>;
  private readonly dc6Cache = new Map<string, Promise<Sprite[]>>();
  private readonly imageCache = new Map<string, Promise<Sprite>>();
  private readonly tableCache = new Map<string, Promise<ExcelTable>>();
  private readonly fonts = new Map<string, Promise<Font>>();
  /** Remap rows for text colors, indexed by TextColor. */
  textRemaps: number[] = [];

  constructor(
    readonly vfs: ArchiveSet,
    readonly r: Renderer,
  ) {}

  async init(onStep?: (what: string) => void): Promise<void> {
    onStep?.('palettes');
    for (const name of PALETTES) {
      const data = await this.vfs.tryRead(`data\\global\\palette\\${name}\\pal.dat`);
      if (data) this.palette[name] = this.r.setPalette(name, decodePaletteDat(data));
    }
    this.r.defaultPalette = this.palette.Units;
    this.pl2 = decodePl2(await this.vfs.read('data\\global\\palette\\act1\\Pal.pl2'));
    this.textRemaps = this.pl2.textColorShifts.map((t, i) => this.r.remaps.set(`text${i}`, t));
    onStep?.('strings');
    // The player's language (a German LoD disc carries data\local\lng\deu).
    this.language = detectLanguage(this.vfs) ?? 'eng';
    this.strings = await loadStrings(this.vfs, this.language);
    onStep?.('animations');
    this.animData = decodeAnimData(await this.vfs.read('data\\global\\AnimData.D2'));
  }

  /** Localized string by key (falls back to the key). */
  str(key: string): string {
    return this.strings.get(key);
  }

  dc6(path: string): Promise<Sprite[]> {
    const key = path.toLowerCase();
    let p = this.dc6Cache.get(key);
    if (!p) {
      p = this.vfs.read(path).then((d) => Sprite.of(decodeDc6(d).frames));
      this.dc6Cache.set(key, p);
    }
    return p;
  }

  /** A full-screen/large picture split into ≤256 px tiles, stitched into one sprite. */
  image(path: string): Promise<Sprite> {
    const key = path.toLowerCase();
    let p = this.imageCache.get(key);
    if (!p) {
      p = this.vfs.read(path).then((d) => new Sprite(stitchDc6(decodeDc6(d).frames)));
      this.imageCache.set(key, p);
    }
    return p;
  }

  /** One 2×2-tiled panel out of a multi-panel DC6 (e.g. invchar6: character = 0, inventory = 4). */
  panel(path: string, firstFrame: number): Promise<Sprite> {
    const key = `${path.toLowerCase()}#${firstFrame}`;
    let p = this.imageCache.get(key);
    if (!p) {
      p = this.vfs.read(path).then((d) => new Sprite(stitchDc6(decodeDc6(d).frames.slice(firstFrame, firstFrame + 4), 2)));
      this.imageCache.set(key, p);
    }
    return p;
  }

  table(name: string): Promise<ExcelTable> {
    const key = name.toLowerCase();
    let p = this.tableCache.get(key);
    if (!p) {
      p = this.vfs.text(`data\\global\\excel\\${name}.txt`).then((t) => new ExcelTable(t));
      this.tableCache.set(key, p);
    }
    return p;
  }

  /** D2 fonts: font6, font8, font16, font24, font30, font42, fontexocet10, fontformal10/11/12, fontingamechat, fontridiculous. */
  font(name: string): Promise<Font> {
    let p = this.fonts.get(name);
    if (!p) {
      p = (async () => {
        const base = `data\\local\\font\\latin\\${name}`;
        const tbl = decodeFontTable(await this.vfs.read(`${base}.tbl`));
        const glyphs = Sprite.of(decodeDc6(await this.vfs.read(`${base}.dc6`)).frames);
        return new Font(this, tbl, glyphs);
      })();
      this.fonts.set(name, p);
    }
    return p;
  }
}
